import { Text } from '@cash-me/react-components'
import styled from 'styled-components'

import { theme } from 'styles/theme'

const { colors, fontSizes, lineHeights, media, radii, space } = theme

type StepCardProps = {
  readonly $order: number
}

export const Section = styled.section`
  box-sizing: border-box;
  width: 100%;
  padding: ${space['spacing-xxxl']} 0;
  background-color: #f4f6fb;

  @media (${media.tablet}) {
    padding: ${space['spacing-giga']} 0;
  }
`

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-md']};

  h2 {
    margin: 0;
    font-size: ${fontSizes['font-size-mobile-heading-2']};
    line-height: ${lineHeights['font-line-height-md']};
    letter-spacing: -0.72px;
  }

  @media (${media.tablet}) {
    gap: ${space['spacing-xl']};

    h2 {
      font-size: ${fontSizes['font-size-heading-2']};
      line-height: ${lineHeights['font-line-height-sm']};
      letter-spacing: -0.96px;
      text-align: center;
    }
  }
`

export const Highlight = styled.span`
  display: inline;
  color: ${colors['blue-background-strong-default']};
  font-weight: 600;

  @media (${media.tablet}) {
    display: block;
    color: ${colors['pink-text-contrast']};
  }
`

export const Introduction = styled(Text)`
  width: 100%;
  margin: 0;

  @media (${media.tablet}) {
    max-width: 1094px;
    margin: 0 auto;
    text-align: center;
  }
`

export const StepList = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: ${space['spacing-xs']};

  @media (${media.tablet}) {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: ${space['spacing-xl']};
    margin-top: 0;

    &::before {
      content: '';
      position: absolute;
      top: 27px;
      right: 12.5%;
      left: 12.5%;
      height: 2px;
      background-color: ${colors['blue-background-strong-default']};
    }
  }
`

export const StepCard = styled.article<StepCardProps>`
  position: sticky;
  top: ${({ $order }) => `calc(${space['spacing-md']} + ${$order * 4.5}rem)`};
  z-index: ${({ $order }) => $order + 1};
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-sm']};
  min-height: 206px;
  padding: ${space['spacing-md']};
  border-top: 1px solid ${colors['neutral-border-weak']};
  border-radius: 30px 30px 0 0;
  background-color: ${colors['neutral-background-default']};

  &:not(:first-child) {
    margin-top: -${space['spacing-sm']};
  }

  &:last-child {
    border-radius: 30px;
  }

  @media (${media.tablet}) {
    position: relative;
    top: auto;
    z-index: 1;
    align-items: center;
    min-height: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    background-color: transparent;
    text-align: center;

    &:not(:first-child) {
      margin-top: 0;
    }

    &:last-child {
      border-radius: 0;
    }
  }
`

export const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${space['spacing-sm']};

  h3 {
    margin: 0;
    font-size: ${fontSizes['font-size-mobile-heading-5']};
  }

  @media (${media.tablet}) {
    flex-direction: column;
    gap: ${space['spacing-md']};
    width: 100%;

    h3 {
      font-size: ${fontSizes['font-size-heading-5']};
    }
  }
`

export const IconWrapper = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${space['spacing-xxxl']};
  height: ${space['spacing-xxxl']};
  padding: ${space['spacing-xxs']};
  border: 2px solid ${colors['blue-background-strong-default']};
  border-radius: ${radii['corner-radius-circular']};
  background-color: #f4f6fb;

  svg {
    width: 100%;
    height: 100%;
  }
`

export const StepDescription = styled(Text)`
  margin: 0;
  font-size: ${fontSizes['font-size-body-1']};
  line-height: ${lineHeights['font-line-height-lg']};

  @media (${media.tablet}) {
    color: ${colors['neutral-text-default']};
    font-size: ${fontSizes['font-size-body-2']};
  }
`

export const Cta = styled.div`
  width: 100%;

  .ds-button {
    display: flex;
    justify-content: center;
    width: 100%;
    height: ${space['spacing-mega']};
    border-radius: ${radii['corner-radius-pill']};
  }

  @media (${media.desktop}) {
    .ds-button {
      width: 230px;
      margin: 0 auto;
    }
  }
`
